import { categories, QTIElementTypes, qtiTypes } from 'common/store/common/constants';
import { setWorkspaceOverlay } from 'common/store/workspaces/actions';
import { WorkspacesOverlayTargets, WorkspacesOverlayTypes } from 'common/store/workspaces/types';
import {
    applyEditedItemProperties,
    resetEditedItemProperties,
    setEditedItemProperties,
    setStructureEditorLock,
} from '../actions';
import storeManager from '../../../store';
import { layoutConstants, stimulusPositions } from '../constants';
import { SectionLayout } from '../../../store/context/types';
import { SelectedQuestionDetailsState } from '../../../store/addStimulusToQuestion/types';

export interface SectionProperties {
    allowStimulusResizing: boolean;
    layout: layoutConstants;
    stimulusSize: number;
    type: string;
    pciTemplateId?: number;
}

const createLayoutFromPosition = (position: string, sectionLayoutProperties: any) => {
    const { layout = layoutConstants.BASIC } = sectionLayoutProperties;
    const prefix = layout.split('-')[0];
    switch (position) {
    case stimulusPositions.NO:
        return prefix;
    case stimulusPositions.LEFT:
    case stimulusPositions.TOP:
        return `${prefix}-${position}`;
    default:
        return layoutConstants.BASIC;
    }
};

const createLayoutFromType = (type: string, sectionLayoutProperties: any) => {
    const { layout } = sectionLayoutProperties;
    const stimulusPosition = layout?.includes('-') ? layout.split('-').pop() : undefined;
    if (type === 'vertical') {
        return `vertical${stimulusPosition ? `-${stimulusPosition}` : ''}`;
    }
    return `basic${stimulusPosition ? `-${stimulusPosition}` : ''}`;
};

const sectionLayoutService = {
    editStimulusSize(featureId: string, width: number, sectionLayoutProperties: any) {
        const newSectionLayoutProperties = { ...sectionLayoutProperties, stimulusSize: width };
        storeManager.dispatch(setEditedItemProperties(featureId, newSectionLayoutProperties));
    },

    changeStimulusType(featureId: string, type: string, sectionLayoutProperties: any, pciTemplateId?: number) {
        const newSectionLayoutProperties: any = { ...sectionLayoutProperties, type };
        if (type === qtiTypes.PCI_STIMULUS && pciTemplateId != null) {
            newSectionLayoutProperties.pciTemplateId = pciTemplateId;
        } else {
            delete newSectionLayoutProperties.pciTemplateId;
        }
        storeManager.dispatch(setEditedItemProperties(featureId, newSectionLayoutProperties));
    },

    changeSectionType(featureId: string, type: string, sectionLayoutProperties: SectionProperties) {
        const newLayoutProperty = createLayoutFromType(type, sectionLayoutProperties) as layoutConstants;
        storeManager.dispatch(setEditedItemProperties(featureId, { ...sectionLayoutProperties, layout: newLayoutProperty }));
    },

    changeSectionProperties(featureId: string, sectionLayoutProperties: SectionProperties) {
        storeManager.dispatch(setEditedItemProperties(featureId, sectionLayoutProperties));
    },

    changeStimulusPosition(featureId: string, position: any, sectionLayoutProperties: any) {
        const newLayoutProperty = createLayoutFromPosition(position, sectionLayoutProperties);
        let type;
        if (position === stimulusPositions.NO) {
            type = null;
        } else if (window.marketplaceProperties.ENABLE_IMPROVED_AUTHOR2_STIMULUS_LAYOUT) {
            // When ENABLE_IMPROVED_AUTHOR2_STIMULUS_LAYOUT enabled, allow null values and disable default type selection
            type = sectionLayoutProperties.type;
        } else {
            type = sectionLayoutProperties.type || qtiTypes.CONTENT_BLOCK;
        }

        const stimulusSize = (
            position === stimulusPositions.NO
                ? -1
                : sectionLayoutProperties.stimulusSize === -1
                    ? sectionLayoutService.getDefaultStimulusSize()
                    : sectionLayoutProperties.stimulusSize
        );
        const allowStimulusResizing = (
            position === stimulusPositions.NO
                ? false
                : sectionLayoutProperties.allowStimulusResizing
        );
        storeManager.dispatch(
            setEditedItemProperties(featureId, {
                ...sectionLayoutProperties,
                layout: newLayoutProperty,
                type,
                stimulusSize,
                allowStimulusResizing,
            })
        );
    },

    changeAllowStimulusResizing(featureId: string, allowStimulusResizing: any, sectionLayoutProperties: any) {
        const newSectionLayoutProperties = { ...sectionLayoutProperties, allowStimulusResizing };
        storeManager.dispatch(setEditedItemProperties(featureId, newSectionLayoutProperties));
    },

    applyEditedItemProperties(
        featureId: string,
        sectionLayoutProperties: SectionProperties,
        stimulus: any,
        section?: any,
        selectedQuestionDetails?: SelectedQuestionDetailsState,
        deleteStimulus?: boolean
    ) {
        const { layout, stimulusSize } = sectionLayoutProperties;
        const isStimulusTypeEdited = selectedQuestionDetails?.questionContentItemId
            ? true
            : sectionLayoutProperties.type && stimulus?.objectType !== sectionLayoutProperties.type;
        const shouldDeleteStimulus = deleteStimulus
            || (stimulus?.objectId && !layout.includes(stimulusPositions.LEFT) && !layout.includes(stimulusPositions.TOP));
        const width = isNaN(stimulusSize)
            ? sectionLayoutService.getDefaultStimulusSize() // user let input empty after manually editing, so fill with default value
            : stimulusSize;
        let newSectionLayoutProperties = { ...sectionLayoutProperties, stimulusSize: width };

        if (selectedQuestionDetails?.questionContentItemId !== undefined && selectedQuestionDetails?.questionContentItemId !== null) {
            newSectionLayoutProperties = { ...sectionLayoutProperties, type: qtiTypes.QUESTION_STIMLULUS };
        }

        storeManager.dispatch(setStructureEditorLock(featureId, true));
        storeManager.dispatch(
            applyEditedItemProperties(
                featureId, newSectionLayoutProperties, isStimulusTypeEdited, shouldDeleteStimulus,
                stimulus?.objectId, section?.objectId, selectedQuestionDetails
            )
        );
    },
    resetEditedItemProperties(featureId: string, objectId?: number) {
        storeManager.dispatch(resetEditedItemProperties(featureId, objectId));
    },

    getSectionStimulus(sectionItems: Array<any>, section?: any) {
        if (section?.qtiElementType === QTIElementTypes.INLINE_SECTION) {
            return section.subItems[0]?.category === categories.STIMULUS ? section.subItems[0] : null;
        }
        return sectionItems[0]?.category === categories.STIMULUS ? sectionItems[0] : null;
    },

    resolveStimulusIdentity(
        stimulus: any,
        pciStimulusTemplates: Array<{ title: string; templateContentItemId: number }> = []
    ): { type: string; pciTemplateId?: number } {
        const isPciObjectType = stimulus?.objectType === qtiTypes.PCI_STIMULUS_OBJECT_TYPE;
        const isLegacyContentBlock = stimulus?.objectType === qtiTypes.CONTENT_BLOCK;
        if (!isPciObjectType && !isLegacyContentBlock) {
            return { type: stimulus?.objectType };
        }

        if (stimulus.pciTemplateId != null) {
            return { type: qtiTypes.PCI_STIMULUS, pciTemplateId: stimulus.pciTemplateId };
        }

        const matchedTemplate = pciStimulusTemplates.find((template) => template.title === stimulus.title);
        if (isPciObjectType) {
            return { type: qtiTypes.PCI_STIMULUS, pciTemplateId: matchedTemplate?.templateContentItemId };
        }
        if (matchedTemplate) {
            return { type: qtiTypes.PCI_STIMULUS, pciTemplateId: matchedTemplate.templateContentItemId };
        }
        return { type: stimulus?.objectType };
    },

    getSectionLayoutSettings(
        contextLayout: any,
        stimulus?: any,
        section?: any,
        pciStimulusTemplates: Array<{ title: string; templateContentItemId: number }> = []
    ) {
        const identity = sectionLayoutService.resolveStimulusIdentity(stimulus, pciStimulusTemplates);
        if (section?.qtiElementType === QTIElementTypes.INLINE_SECTION) {
            return { ...section?.sectionLayout, ...identity };
        }
        return { ...contextLayout, ...identity };
    },

    getSelectedStimulus(sectionItems: Array<any>, section?: any) {
        if (section?.qtiElementType === QTIElementTypes.INLINE_SECTION) {
            const selectedItem = sectionItems.find((item: any) => item.objectId === section.objectId);
            return selectedItem.subItems[0]?.category === categories.STIMULUS ? selectedItem.subItems[0] : null;
        }

        return (sectionItems[0]?.category || sectionItems[0]?.type) === categories.STIMULUS ? sectionItems[0] : null;
    },

    getStimulusPositionFromLayout(layout: layoutConstants) {
        return layout.includes('-') ? layout.split('-').pop() : stimulusPositions.NO;
    },

    getSectionTypeFromLayout(layout: layoutConstants) {
        let sectionType = layout.split('-', 1)[0];
        if (sectionType.length === 0) sectionType = layoutConstants.BASIC;
        return sectionType;
    },

    getDefaultStimulusSize() {
        return window.marketplaceProperties.DEFAULT_STIMULUS_SIZE || 0;
    },

    isLayoutWithStimulus(layout: layoutConstants) {
        return [
            layoutConstants.BASIC_LEFT,
            layoutConstants.VERTICAL_LEFT,
            layoutConstants.VERTICAL_TOP,
        ].includes(layout);
    },

    openStimulusAndLayoutModal(featureId: string, section?: any, sectionLayout?: SectionLayout, shouldRemoveAnimation? : boolean) {
        const workspaceOverlayName = WorkspacesOverlayTypes.STIMULUS_LAYOUTS;
        const workspaceTitle = '';
        const options = {
            featureId,
            section,
            sectionLayout,
            withoutIcon: true,
            closeButtonClasses: 'closeButtonStimulusLayoutOverlay',
            ...(shouldRemoveAnimation && { removeAnimationClasses: 'noAnimation' }),
        };
        storeManager.dispatch(
            setWorkspaceOverlay(
                workspaceOverlayName,
                WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
                {
                    workspaceTitle,
                    options,
                }
            )
        );
    },

    openQuestionDeliveryModal(featureId: string, section: any, sectionLayout: SectionLayout) {
        const workspaceOverlayName = WorkspacesOverlayTypes.QUESTION_DELIVERY;
        const workspaceTitle = '';
        const options = {
            featureId,
            section,
            sectionLayout,
            withoutIcon: true,
            closeButtonClasses: 'closeButtonStimulusLayoutOverlay',
        };
        storeManager.dispatch(
            setWorkspaceOverlay(
                workspaceOverlayName,
                WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
                {
                    workspaceTitle,
                    options,
                }
            )
        );
    },

    openContentBlockDeliveryModal(featureId: string, section: any, sectionLayout: SectionLayout) {
        const workspaceOverlayName = WorkspacesOverlayTypes.CONTENT_BLOCK_DELIVERY;
        const workspaceTitle = '';
        const options = {
            featureId,
            section,
            sectionLayout,
            withoutIcon: true,
            closeButtonClasses: 'closeButtonStimulusLayoutOverlay',
        };
        storeManager.dispatch(
            setWorkspaceOverlay(
                workspaceOverlayName,
                WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
                {
                    workspaceTitle,
                    options,
                }
            )
        );
    },

    isNewStimulusLayoutEnabled() {
        return window.marketplaceProperties.ENABLE_IMPROVED_AUTHOR2_STIMULUS_LAYOUT;
    },

    openAddStimulusFromBankModal(contentRef?: string, contextId?: number, parentId?: number) {
        const workspaceOverlayName = WorkspacesOverlayTypes.ADD_STIMULUS_FROM_BANK;
        const workspaceTitle = 'Select stimulus from Bank';
        const options = {
            hideTitle: true,
            skipContext: true,
            contextId,
            contentRef: contentRef || QTIElementTypes.QUESTION_REF,
            parentId,
            withoutIcon: true,
            closeButtonClasses: 'closeButtonStimulusLayoutOverlay',
            removeAnimationClasses: 'noAnimation',
        };
        storeManager.dispatch(
            setWorkspaceOverlay(
                workspaceOverlayName,
                WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
                {
                    workspaceTitle,
                    options,
                }
            )
        );
    },

};

export default sectionLayoutService;
